import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useEntry, Loading, ErrorBox, PriorityChip } from "./entryShared";

const TRANSFER = { high: "text-emerald-700", partial: "text-amber-600", low: "text-orange-600", none: "text-red-600", "n/a": "text-slate-400" };

const ConvertButton = ({ caseId, row }) => {
  const [state, setState] = useState("idle");
  const convert = () => {
    setState("busy");
    axios.post(`${API}/access-cases/${caseId}/actions`, {
      activity: row.action,
      strategic_rationale: `Closes evidence gap — ${row.gap}`,
      evidence_gap_addressed: row.label,
      access_barrier_addressed: row.gap,
      linked_gap_rows: [row.id],
      priority: row.priority,
      source: { type: "gap_matrix", ref: row.id, label: row.label },
    }).then(() => { setState("done"); toast.success(`Action created from "${row.label}" — see the Actions tab`); })
      .catch((e) => { setState("idle"); toast.error(e.response?.data?.detail || e.message); });
  };
  if (state === "done") return <span className="text-[9px] font-bold uppercase text-emerald-700">Action created</span>;
  return (
    <button data-testid={`convert-gap-${row.row_id}`} onClick={convert} disabled={state === "busy"}
      className="px-2 py-1 text-[9px] font-bold uppercase tracking-wide bg-slate-900 text-white hover:bg-slate-700 disabled:opacity-50 whitespace-nowrap">
      {state === "busy" ? "…" : "→ Action"}
    </button>
  );
};

export const CaseGaps = ({ caseId }) => {
  const { data, error } = useEntry(caseId, "/gap-matrix");
  if (error) return <ErrorBox error={error} />;
  if (!data) return <Loading />;
  const pc = data.priority_counts || {};
  return (
    <div data-testid="case-gaps" className="mt-4">
      <div className="flex flex-wrap gap-5 mb-4" data-testid="gap-priority-counts">
        {["critical", "high", "medium", "low"].map((p) => (
          <div key={p} className="flex items-center gap-2"><PriorityChip value={p} /><span className="font-heading font-bold text-slate-900">{pc[p] ?? 0}</span></div>
        ))}
        {data.closed > 0 && (
          <div className="flex items-center gap-2" data-testid="gap-closed-count">
            <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide bg-emerald-700 text-white">Closed</span>
            <span className="font-heading font-bold text-slate-900">{data.closed}</span>
          </div>
        )}
      </div>
      {!data.rows?.length ? (
        <p className="text-xs text-slate-400 border border-dashed border-slate-300 bg-white px-4 py-8 text-center" data-testid="gaps-empty">
          Evidence gap matrix not yet developed for this case.</p>
      ) : (
      <div className="overflow-x-auto border border-slate-200 bg-white">
        <table className="w-full text-left">
          <thead><tr className="bg-slate-900 text-white text-[9px] uppercase tracking-widest">
            <th className="px-3 py-2.5">Evidence area</th><th className="px-3 py-2.5">Global evidence</th><th className="px-3 py-2.5">Target-market evidence</th>
            <th className="px-3 py-2.5">Transferability</th><th className="px-3 py-2.5">Gap</th><th className="px-3 py-2.5">Action</th>
            <th className="px-3 py-2.5">Priority</th><th className="px-3 py-2.5">Confidence</th><th className="px-3 py-2.5">Execute</th>
          </tr></thead>
          <tbody>
            {(data.rows || []).map((r) => (
              <tr key={r.id} className={`border-t border-slate-100 align-top ${r.status === "closed" ? "opacity-50" : ""}`} data-testid={`gap-row-${r.row_id}`}>
                <td className="px-3 py-2 text-[11px] font-bold text-slate-800 min-w-[130px]">{r.label}</td>
                <td className="px-3 py-2 text-[10px] text-slate-600 min-w-[140px]">{r.global_evidence}</td>
                <td className={`px-3 py-2 text-[10px] min-w-[120px] ${r.local_evidence === "None" ? "text-slate-400" : "text-slate-700"}`}>{r.local_evidence}</td>
                <td className={`px-3 py-2 text-[10px] font-bold uppercase ${TRANSFER[r.transferability] || "text-slate-400"}`}>{r.transferability}</td>
                <td className="px-3 py-2 text-[10px] text-slate-600 min-w-[140px]">{r.gap}</td>
                <td className="px-3 py-2 text-[10px] text-emerald-700 min-w-[140px]">{r.action}</td>
                <td className="px-3 py-2">{r.status === "closed"
                  ? <span className="px-2 py-0.5 text-[10px] font-bold uppercase bg-emerald-700 text-white">Closed</span>
                  : <PriorityChip value={r.priority} />}</td>
                <td className="px-3 py-2 text-[10px] uppercase text-slate-500">{r.confidence}</td>
                <td className="px-3 py-2">{r.status !== "closed" && <ConvertButton caseId={caseId} row={r} />}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
};
